import React, { useLayoutEffect, useState } from 'react';
import { horizonText } from '../../utils/portedHorizon';

// 🔍📌 Under ONE of the resident's messages, on the owner's desktop place page
// (PORTED_HOME_HANDOFF_2026_09_25 W2c; the look is mockup v9's .chipbtn and
// .receipt): "🔍 why" opens a printed slip of what really went into that
// reply (the server's receipt: every item was found in the exact prompt the
// reply was written from), and "📌 pin to the board" puts the line on the
// cork board. Owner only: the page passes this nothing anywhere else, and the
// receipt itself only ever arrives over the owner's secret-proven POST.
//
// The virtual feed keys rows by INDEX, so the page renders this with
// key={actionId}: a slot that starts showing a different message gets a fresh
// component (the open slip never lands under the wrong line).
//
// Also here: the horizon row (utils/portedHorizon), so the feed's two
// pseudo-row branches render one small thing each.
//
// Class names avoid every substring the generated themes target
// ([class*='chip'|'button'|'btn'|'tag'|'card'|'message'...]), the buttons carry
// the theme shield (data-prop-ui) plus doubled-class armor, and the CSS goes
// into <head> ONCE (a lived-in feed holds dozens of his lines).
const SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';
const SERIF = '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif';
export const PLACE_LINE_CSS = `
.pwl, .pwl *, .pwl-hz { box-sizing: border-box; }
.pwl { display: block; flex: 1 1 100%; width: 100%; padding: 0 8px; text-align: left; font-family: ${SANS}; }
.pwl-acts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 7px; }
.pwl .pwl-b.pwl-b { all: unset; box-sizing: border-box; display: inline-flex; align-items: center; cursor: pointer; --prop-bg: rgba(255,246,235,.05); font: 600 11px/1 ${SANS} !important; letter-spacing: normal !important; text-transform: none !important; padding: 5px 8px !important; margin: 0 !important; width: auto !important; height: auto !important; min-width: 0 !important; min-height: 0 !important; border-radius: 999px !important; border: 1px solid rgba(255,236,210,.2) !important; background: rgba(255,246,235,.05) !important; color: rgba(255,238,216,.72) !important; box-shadow: none !important; text-shadow: none !important; transform: none !important; opacity: 1 !important; }
.pwl .pwl-b.pwl-b:hover { border-color: rgba(240,172,85,.55) !important; color: #FFE1B0 !important; }
.pwl .pwl-b.pwl-b:focus-visible { outline: 2px solid #FFE1B0 !important; outline-offset: 2px; }
.pwl .pwl-b.pwl-b.pwl-done, .pwl .pwl-b.pwl-b.pwl-done:hover { color: #9fd8b0 !important; border-color: rgba(127,206,154,.45) !important; cursor: default; }
.pwl-slip { margin-top: 8px; max-width: 520px; padding: 9px 11px; border-radius: 6px; background: #FBF3DC; color: #3a2a2e; font: 500 12.5px/1.5 ${SANS}; box-shadow: 0 3px 10px rgba(0,0,0,.3); }
.pwl-eyebrow { font: 600 10px/1 ${MONO}; letter-spacing: .14em; text-transform: uppercase; color: #8a6a3a; margin-bottom: 6px; }
.pwl-ri { display: grid; grid-template-columns: 20px minmax(0, 1fr); gap: 6px; }
.pwl-ri > span:last-child { overflow-wrap: anywhere; }
.pwl-foot { margin-top: 6px; font: italic 11px/1.3 ${SERIF}; color: #7a6660; }
.pwl-hz { display: flex; align-items: center; gap: 10px; width: 100%; margin: 6px 0; padding: 0 8px; font: italic .8rem/1.2 ${SERIF}; color: rgba(255,225,176,.62); }
.pwl-hz::before, .pwl-hz::after { content: ''; flex: 1; border-top: 1px dashed rgba(255,225,176,.35); }
`;
const STYLE_ID = 'ported-line-tools-css';

// Idempotent, and re-checks the node (a test or a theme swap can clear <head>).
export function ensurePlaceLineStyle(doc = (typeof document !== 'undefined' ? document : null)) {
  try {
    if (!doc || !doc.head || doc.getElementById(STYLE_ID)) return;
    const s = doc.createElement('style');
    s.id = STYLE_ID;
    s.textContent = PLACE_LINE_CSS;
    doc.head.appendChild(s);
  } catch (e) { /* unstyled chips still work */ }
}

const nameOf = (residentName) => String(residentName || '').trim() || 'Your resident';

export default function PlaceLineTools({
  actionId,
  residentName = null,
  receipt,            // [{ k, i, t }] from the owner's why fetch · null/undefined = none (no 🔍)
  pinState = 'none',  // 'none' | 'pending' (pinned, not heard yet) | 'on'
  onPin = null,       // (actionId) => void
  onOpenChange = null, // (open, actionId) => void: the page may re-snap the feed
}) {
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => { ensurePlaceLineStyle(); }, []);
  const items = Array.isArray(receipt) ? receipt.filter((r) => r && String(r.t || '').trim()) : [];
  const hasWhy = items.length > 0;
  const pinShown = !!onPin || pinState === 'pending' || pinState === 'on';
  if (!hasWhy && !pinShown) return null;
  const name = nameOf(residentName);
  const slipId = `pwl-slip-${String(actionId || '').replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const showSlip = hasWhy && open;
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (onOpenChange) onOpenChange(next, actionId);
  };
  const pinLabel = pinState === 'on' ? 'on the board ✓'
    : pinState === 'pending' ? `pinned · ${name} sees it next time` : '📌 pin to the board';
  const pinned = pinState === 'on' || pinState === 'pending';
  return (
    <div className="pwl" data-place-line={actionId || ''}>
      <div className="pwl-acts">
        {hasWhy ? (
          <button type="button" data-prop-ui="" className="pwl-b pwl-why" aria-expanded={showSlip} aria-controls={slipId} onClick={toggle}>🔍 why</button>
        ) : null}
        {pinShown ? (
          <button
            type="button"
            data-prop-ui=""
            className={`pwl-b pwl-pin${pinned ? ' pwl-done' : ''}`}
            disabled={pinned || !onPin}
            onClick={() => { if (!pinned && onPin) onPin(actionId); }}
          >{pinLabel}</button>
        ) : null}
      </div>
      {showSlip ? (
        <div className="pwl-slip" id={slipId} role="note">
          <div className="pwl-eyebrow">What went into this</div>
          {items.map((r, n) => (
            <div className="pwl-ri" key={`${r.k || 'x'}:${n}`} data-why-kind={r.k || ''}>
              <span aria-hidden="true">{r.i || '•'}</span>
              <span>{String(r.t)}</span>
            </div>
          ))}
          <div className="pwl-foot">only you see what {name} remembered.</div>
        </div>
      ) : null}
    </div>
  );
}

// ↑ The horizon row: where his view of the chat ends (a plain full-width
// printed line; no <hr>, no button, no theme-targeted class substring).
// 🧠 `mem` (wave 2 C2): the server said everything above lives on as memory.
export function PlaceHorizon({ residentName = null, mem = false }) {
  useLayoutEffect(() => { ensurePlaceLineStyle(); }, []);
  return <div className="pwl-hz" data-ported-horizon="" data-mem={mem === true ? '' : undefined}>{horizonText(residentName, mem === true)}</div>;
}
